/**
 * UnsplashComplianceDemo — /unsplash-demo
 *
 * Public evidence page for Unsplash's API-terms compliance review.
 *
 * Renders four photos fetched live via /api/unsplash/search with:
 *   • "Photo by <Name> on Unsplash" attribution below every image
 *   • Photographer profile link (utm_source=<app>&utm_medium=referral)
 *   • Unsplash.com link with the same UTM
 *   • Fires /api/unsplash/track-use exactly once per image on mount,
 *     which in turn hits Unsplash's /photos/:id/download endpoint —
 *     satisfying the API guideline that a "download" tracking hit is
 *     sent every time the app actually uses a photo.
 *
 * Not marketed. Not linked from nav. Sole purpose: give Unsplash a
 * verifiable URL to inspect.
 */
import { useEffect, useState } from "react";
import UnsplashPhoto from "@/components/UnsplashPhoto";

const API = process.env.REACT_APP_BACKEND_URL;

const DEMO_QUERIES = [
  { key: "airport",   label: "Airport terminal",  q: "airport terminal" },
  { key: "aircraft",  label: "Aircraft on tarmac", q: "aircraft tarmac"  },
  { key: "food",      label: "Farmers market",     q: "farmers market"   },
  { key: "kerala",    label: "Kerala backwaters",  q: "kerala backwaters"},
];

export default function UnsplashComplianceDemo() {
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Fire all searches in parallel — faster + no cancellation races
        const entries = await Promise.all(
          DEMO_QUERIES.map(async (t) => {
            const r = await fetch(
              `${API}/api/unsplash/search?q=${encodeURIComponent(t.q)}&per_page=1`,
            );
            if (!r.ok) return [t.key, { _error: `HTTP ${r.status}` }];
            const j = await r.json();
            return [t.key, j?.results?.[0] || { _error: "no_results" }];
          }),
        );
        setPhotos(Object.fromEntries(entries));
      } catch (e) {
        setErr(e?.message || "load_failed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="unsplash-compliance-demo">
      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
          Compliance evidence page
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold mb-4">
          Unsplash API compliance — live demo
        </h1>
        <p className="text-sm text-gray-700 max-w-3xl mb-2">
          This page fetches four photos from Unsplash on load via our compliant
          proxy at{" "}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            /api/unsplash/search
          </code>
          . Every rendered image displays{" "}
          <em>&quot;Photo by &lt;Name&gt; on Unsplash&quot;</em> with links to
          the photographer profile and unsplash.com, both carrying our
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded mx-1">
            utm_source=social_pulse
          </code>{" "}
          and
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded mx-1">
            utm_medium=referral
          </code>{" "}
          parameters.
        </p>
        <p className="text-sm text-gray-700 max-w-3xl mb-8">
          On every render, the frontend also fires{" "}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            POST /api/unsplash/track-use
          </code>
          , which server-side hits the photo's{" "}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            /photos/{"{id}"}/download
          </code>{" "}
          endpoint — satisfying the API guideline that download activity is
          reported for every real use of a photo. Every such hit is
          audit-logged in our database.
        </p>

        {loading && (
          <div className="text-sm text-gray-500 py-8" data-testid="unsplash-demo-loading">
            Loading photos from Unsplash …
          </div>
        )}
        {err && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 mb-4" data-testid="unsplash-demo-error">
            Load error: {err}
          </div>
        )}

        <div className="grid gap-8 sm:grid-cols-2">
          {DEMO_QUERIES.map((t) => {
            const p = photos[t.key];
            if (!p) return null;
            if (p._error) {
              return (
                <div key={t.key} className="p-4 rounded-lg border border-red-200 bg-red-50">
                  <div className="text-xs uppercase tracking-widest text-red-600 mb-1">
                    {t.label}
                  </div>
                  <div className="text-xs text-red-700">Search error: {p._error}</div>
                </div>
              );
            }
            return (
              <figure key={t.key} className="rounded-lg overflow-hidden bg-gray-50" data-testid={`demo-photo-${t.key}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1 px-2 pt-2">
                  {t.label} · search q=&quot;{t.q}&quot; · photo_id={p.photo_id}
                </div>
                <UnsplashPhoto
                  photo={p}
                  trackContext={`compliance-demo/${t.key}`}
                  className="w-full h-64 object-cover"
                  captionAlign="left"
                />
              </figure>
            );
          })}
        </div>

        <hr className="my-10 border-gray-200" />
        <div className="text-xs text-gray-500 leading-relaxed">
          <p className="mb-2">
            <strong>How this proves compliance:</strong>
          </p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>
              Our access key is server-side only — never exposed to the browser. All
              Unsplash API traffic is proxied through <code>/api/unsplash/*</code>.
            </li>
            <li>
              Every photo response includes <code>photo_id</code>, the exact{" "}
              <code>download_location</code> URL Unsplash returned, and the
              photographer&apos;s canonical profile URL.
            </li>
            <li>
              Attribution is rendered by a single{" "}
              <code>&lt;UnsplashPhoto&gt;</code> component used consistently
              across the site — impossible to render an Unsplash image
              without its credit strip.
            </li>
            <li>
              Every hardcoded <code>images.unsplash.com/photo-*</code> URL in
              our source code and MongoDB documents has been removed. There
              is no other pathway by which our app can display an Unsplash
              image except via this compliant proxy.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
